import keys from '@debbie/common/dist/cache/keys.js';
import { PermissionLevels } from '@debbie/common/dist/index.js';
import { UserState } from '@debbie/common/dist/users/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as creditorsAPI from '@debbie/cortex/dist/api/creditors.js';
import { creditorsEntity, useCreditor, } from '@debbie/cortex/dist/creditors/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useUsers } from '@debbie/cortex/dist/users/index.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { Plus, X } from '../Icons/index.js';
import Modal from '../Modal.js';
import UserPreview from '../UserPreview.js';
import CreateUserSettings from '../users/CreateUserSettings.js';
import UserSelectModal from '../users/UserSelectModal.js';
const isClientUserOnCreditor = (creditorId) => (user) => user.role === PermissionLevels.client &&
    [UserState.ACTIVE, UserState.INVITED].includes(user.state) &&
    user.creditors.some((c) => c.id === creditorId);
const ClientNoteRecipients = (props) => {
    const lang = useSelector((s) => s.lang);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const usersLoadable = useUsers();
    const allUsers = h.hasData(usersLoadable) ? usersLoadable.data : null;
    const creditorLoadable = useCreditor(props.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const availableUsers = useMemo(() => allUsers ? allUsers.filter(isClientUserOnCreditor(props.creditorId)) : [], [allUsers, props.creditorId]);
    const selectedUsers = useMemo(() => {
        const selected = new Set(props.selectedIds);
        return availableUsers.filter((u) => selected.has(u.id));
    }, [availableUsers, props.selectedIds]);
    // Ids invited from this component that the (stale, still reloading) users
    // list has not delivered yet. They are exempt from the eligibility strip
    // below until they appear in the list — otherwise a freshly invited
    // recipient would be dropped in the window between user creation and the
    // users-cache reload.
    const pendingInvitedIds = useRef(new Set());
    // Once users have loaded, drop any selected id that is not an eligible client
    // user of this creditor. Such ids render no chip (so the caseworker cannot
    // see or remove them) yet would still be sent — keep selection in sync with
    // what is actually shown.
    useEffect(() => {
        if (!allUsers) {
            return;
        }
        for (const id of [...pendingInvitedIds.current]) {
            if (allUsers.some((u) => u.id === id)) {
                pendingInvitedIds.current.delete(id);
            }
        }
        const eligible = new Set(selectedUsers.map((u) => u.id));
        const keep = props.selectedIds.filter((id) => eligible.has(id) || pendingInvitedIds.current.has(id));
        if (keep.length !== props.selectedIds.length) {
            props.onChange(keep);
        }
    }, [allUsers, props.selectedIds, selectedUsers]);
    const remove = (id) => {
        props.onChange(props.selectedIds.filter((existing) => existing !== id));
    };
    const add = (user) => {
        if (user && !props.selectedIds.includes(user.id)) {
            props.onChange([...props.selectedIds, user.id]);
        }
        setPickerOpen(false);
    };
    const setPrimaryContact = useMutation((createdUserId) => creditorsAPI.update(props.creditorId, { contactId: createdUserId }), {
        onSuccess: () => q.invalidate(keys.write.modify(creditorsEntity, props.creditorId)),
        onError: (err) => errorNotification(lang, err, 'update', lang.errorEntities.creditor),
    });
    return (React.createElement(Wrapper, null,
        React.createElement(Label, null, lang.interactions.recipients),
        React.createElement(Chips, null,
            selectedUsers.map((user) => (React.createElement(Chip, { key: user.id },
                React.createElement(UserPreview, { user: user, noTooltip: true }),
                React.createElement(Name, null,
                    user.firstName,
                    " ",
                    user.lastName),
                React.createElement(RemoveButton, { type: "button", onClick: () => remove(user.id), "aria-label": lang.interactions.removeRecipient },
                    React.createElement(X, { size: "14" }))))),
            props.selectedIds.length === 0 && (React.createElement(EmptyNotice, null, lang.interactions.noRecipientsSelected)),
            React.createElement(AddButton, { type: "button", onClick: () => setPickerOpen(true) },
                React.createElement(Plus, { size: "14" }),
                React.createElement("span", null, lang.interactions.addRecipient))),
        pickerOpen && (React.createElement(UserSelectModal, { header: lang.interactions.addRecipient, filter: (user) => isClientUserOnCreditor(props.creditorId)(user) &&
                !props.selectedIds.includes(user.id), emptyText: lang.interactions.noClientUsersOnCreditor, invite: {
                label: lang.interactions.inviteClientUser,
                onInvite: () => {
                    setPickerOpen(false);
                    setInviteOpen(true);
                },
            }, onClose: () => setPickerOpen(false), onSelect: add })),
        inviteOpen && (React.createElement(Modal, { onToggle: () => setInviteOpen(false), header: lang.users.newUser, maxWidth: "400px" },
            React.createElement(CreateUserSettings, { role: PermissionLevels.client, creditorIds: [props.creditorId], onCreate: (createdUserId) => {
                    // The new user is created as a client user on this creditor, so
                    // it becomes an eligible recipient on its own.
                    setInviteOpen(false);
                    pendingInvitedIds.current.add(createdUserId);
                    props.onChange([...props.selectedIds, createdUserId]);
                    // A creditor without a primary contact gets the invited user as
                    // its contact by default. An existing contact is never
                    // reassigned from here.
                    if (creditor && creditor.contactId == null) {
                        setPrimaryContact.mutate(createdUserId);
                    }
                } })))));
};
export default ClientNoteRecipients;
const Wrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  flex-wrap: wrap;
  margin-bottom: ${THEME.GRID_SPACING}px;
`;
const Label = styled.span `
  font-size: 12px;
  font-weight: 600;
  color: ${THEME.COLOR_PRIMARY};
  flex-shrink: 0;
`;
const Chips = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  align-items: center;
  min-width: 0;
`;
const Chip = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(117, 104, 246, 0.08);
  border-radius: 6px;
  padding: 4px 4px 4px 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${THEME.COLOR_PRIMARY};
`;
const Name = styled.span `
  line-height: 1;
`;
const EmptyNotice = styled.span `
  font-size: 12px;
  color: ${THEME.DARK_COLOR_SECONDARY};
`;
const RemoveButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 2px;
  border-radius: 4px;
  color: ${THEME.DARK_COLOR_SECONDARY};
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: ${THEME.DANGEROUS};
  }
`;
const AddButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${THEME.COLOR_ACCENT};
  cursor: pointer;
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(117, 104, 246, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${THEME.COLOR_ACCENT};
    outline-offset: 1px;
  }
`;
//# sourceMappingURL=ClientNoteRecipients.js.map